/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createReleaseApp } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type ReleaseAppDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function ReleaseAppDialog({ actionDialogReturn, onNavigate: _onNavigate }: ReleaseAppDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const {
    workspace,
    activeOrganization,
    activeProject,
    setActiveReleaseAppId,
    setWorkspaceVersion,
  } = useWorkspace();

  const { organizations, apps } = workspace;

  if (!actionDialog || actionDialog.kind !== 'release-app') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit =
    actionDialog.name.trim().length >= 2 &&
    actionDialog.organizationId.trim().length > 0 &&
    actionDialog.projectId.trim().length > 0;
  const selectedOrganization =
    organizations.find((org) => org.id === actionDialog.organizationId) ?? activeOrganization;
  const selectedProject =
    apps.find((app) => app.id === actionDialog.projectId) ?? activeProject;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'release-app' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      if (activeOrganization && activeProject) {
        const releaseApp = await createReleaseApp(session.accessToken, {
          organizationId: activeOrganization.id,
          projectId: activeProject.id,
          platform: actionDialog.platform,
          name: actionDialog.name.trim(),
          environment: actionDialog.environment || undefined
        });
        setActiveReleaseAppId(releaseApp.releaseAppId);
        setWorkspaceVersion((v) => v + 1);
        toast('success', 'App store track created');
      } else {
        setDialogError('Choose an organization and project before creating an app store track.');
        return;
      }
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">App store versions</div>
            <h3>New app store track</h3>
            <p>Create a platform-specific release track for this project.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <div className="settingsSplit">
            <label className="settingsField">
              <span>Organization</span>
              <input value={selectedOrganization?.name || 'Selected organization'} disabled />
            </label>
            <label className="settingsField">
              <span>Project</span>
              <input value={selectedProject?.name || 'Selected project'} disabled />
            </label>
          </div>
          <label className="settingsField">
            <span>Platform</span>
            <select
              value={actionDialog.platform}
              onChange={(e) => setActionDialog({ ...actionDialog, platform: e.target.value as 'ios' | 'android' })}
            >
              <option value="ios">iOS</option>
              <option value="android">Android</option>
            </select>
          </label>
          <label className="settingsField">
            <span>Name</span>
            <input
              value={actionDialog.name}
              onChange={(e) => setActionDialog({ ...actionDialog, name: e.target.value })}
              placeholder="Acme iOS"
              autoFocus
            />
            <p className="fieldHint">This label should feel like a release track, not a system key.</p>
          </label>
          <label className="settingsField">
            <span>Environment</span>
            <select
              value={actionDialog.environment}
              onChange={(e) =>
                setActionDialog({
                  ...actionDialog,
                  environment: e.target.value as typeof actionDialog.environment
                })
              }
            >
              <option value="">Not set</option>
              <option value="production">production</option>
              <option value="staging">staging</option>
              <option value="dev">dev</option>
              <option value="internal">internal</option>
            </select>
            <p className="fieldHint">
              One app store track per platform per environment — bundle ID and
              signing cert differ between production and staging, so they need
              separate tracks here.
            </p>
          </label>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Creating…' : 'Create app'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { ReleaseAppDialog };
